/**
 * Pure helpers for client-offer-review-shell.tsx — extracted to keep the
 * component file within the lint line/complexity budget.
 */
import type { ClientDecision } from '@ooh/ui/components/client-offer-view';
import type { MapPin } from '@ooh/ui/components/map-view';
import type { ClientDiffItem } from '@ooh/contracts';
import { roundMoney } from '@ooh/contracts';
import {
  summarizeSelectedPeriods,
  type ClientOfferReviewCustomPeriodItem,
  type ClientOfferReviewDiffItem,
  type ClientOfferReviewPeriod,
  type ClientOfferReviewUnit,
} from '../../client-offer-review';
import { hasCustomPeriodSelection } from './period-selection-utils';

/** Maps the internal diff-row shape onto the card's simplified type/label/detail shape. */
export function mapClientDiffItemsToReview(diff: ClientDiffItem[]): ClientOfferReviewDiffItem[] {
  return diff.map((item) => ({
    type: item.type === 'changed' ? 'price' : item.type,
    label: item.description,
    detail: '',
  }));
}

export interface OfferReviewAggregates {
  avail: ClientOfferReviewUnit[];
  accU: ClientOfferReviewUnit[];
  rejU: ClientOfferReviewUnit[];
  pendU: ClientOfferReviewUnit[];
  accValue: number;
  totalNet: number;
  soldCount: number;
  pctAcc: number;
  pctRej: number;
  pctPend: number;
}

/**
 * Decision/value aggregates computed over the FULL unit set — never the current page.
 *
 * `totalNet` (the "Suma netto" headline stat) is populated from client-accepted units
 * only — rejected, undecided, and sold units are excluded, so it reads 0 until the
 * client accepts something. It currently coincides with `accValue`; both fields are
 * kept because they serve distinct call sites (headline stat vs. footer/status/modal
 * consumers) and merging them would touch more than this fix requires.
 *
 * Each accepted unit's price is rounded to the nearest grosz before being summed
 * (`roundMoney`, @ooh/contracts) so the total always equals the sum of the
 * per-unit prices as they're displayed, instead of a raw float sum that can
 * drift from that sum by a grosz or more.
 */
export function computeOfferReviewAggregates(
  units: ClientOfferReviewUnit[],
  decisions: Record<string, ClientDecision>,
): OfferReviewAggregates {
  const avail = units.filter((u) => !u.sold);
  const accU = avail.filter((u) => decisions[u.id] === 'accepted');
  const rejU = avail.filter((u) => decisions[u.id] === 'rejected');
  const pendU = avail.filter((u) => !decisions[u.id]);
  const accValue = accU.reduce((s, u) => s + roundMoney(u.price ?? 0), 0);
  const totalNet = accValue;
  const soldCount = units.length - avail.length;
  const pct = (n: number) => Math.round((n / Math.max(avail.length, 1)) * 100);
  const pctAcc = pct(accU.length);
  const pctRej = pct(rejU.length);
  return {
    avail,
    accU,
    rejU,
    pendU,
    accValue,
    totalNet,
    soldCount,
    pctAcc,
    pctRej,
    pctPend: 100 - pctAcc - pctRej,
  };
}

/**
 * Units select-all should mark accepted — excludes canAccept: false (zero pickable
 * periods) so v2's "select all" (onConfirmSelectAll in client-offer-review-shell.tsx)
 * matches the per-unit Accept button's own accept-disabled gate, instead of silently
 * marking a unit accepted whose decision would be dropped server-side at submit. Also
 * excludes locked units (carried forward, already binding-accepted — offer-decision-
 * carry-forward) — select-all has nothing new to decide for them.
 */
export function selectableForAcceptAll(avail: ClientOfferReviewUnit[]): ClientOfferReviewUnit[] {
  return avail.filter((u) => u.canAccept !== false && u.locked !== true);
}

/** Marker color category for a unit's map pin — sold/accepted/rejected win over its format. */
export function markerCategory(unit: ClientOfferReviewUnit, decision: ClientDecision): string {
  if (unit.sold) return 'sold';
  if (decision === 'accepted') return 'accepted';
  if (decision === 'rejected') return 'rejected';
  return unit.formatKey === 'digital' ? 'digital' : 'other';
}

/** Map pins over the FULL unit set — null-coordinate units are listed but never pinned (CU-86cay892c). */
export function buildReviewMapPins(
  units: ClientOfferReviewUnit[],
  decisions: Record<string, ClientDecision>,
): MapPin[] {
  return units
    .filter(
      (u): u is ClientOfferReviewUnit & { lat: number; lng: number } =>
        u.lat != null && u.lng != null,
    )
    .map((u) => ({
      id: u.id,
      latitude: u.lat,
      longitude: u.lng,
      category: markerCategory(u, decisions[u.id] ?? null),
    }));
}

/**
 * Client-safe custom-period breakdown — units by GRW number, address and city,
 * no supplier identity (task 3.7). GRW is the id the unit card shows, so a row
 * here can be matched back to a card; the supplier number deliberately is not
 * surfaced.
 */
export function buildCustomPeriodItems(
  accU: ClientOfferReviewUnit[],
  selectedPeriods: Record<string, string[]>,
  defaultPeriodIdsByUnitId: Map<string, string[]>,
  periods: ClientOfferReviewPeriod[],
): ClientOfferReviewCustomPeriodItem[] {
  return accU
    .filter((unit) =>
      hasCustomPeriodSelection(
        { [unit.id]: selectedPeriods[unit.id] ?? [] },
        { [unit.id]: defaultPeriodIdsByUnitId.get(unit.id) ?? [] },
      ),
    )
    .map((unit) => {
      const selectedIds = selectedPeriods[unit.id] ?? [];
      return {
        id: unit.id,
        grw: unit.grw,
        address: unit.address,
        city: unit.city,
        periodSummary: summarizeSelectedPeriods(periods, selectedIds),
        selectedCount: selectedIds.length,
        totalCount: periods.length,
      };
    });
}
